import { useEffect, useState } from "react";
import { Mail, Send, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useAuth } from "../context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function DigestSettings() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { api.get("/digest").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.eligible) return null;
  const save = async (body) => {
    try { const { data } = await api.put("/digest", body); setD(data); toast.success("Saved"); } catch (e) { toast.error(errorMessage(e)); }
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/digest/test"); data.sent ? toast.success(`Test digest sent to ${data.to}`) : toast.info(data.detail); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const addEmail = () => {
    const e = draft.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email address"); return; }
    if ((d.extra_emails || []).includes(e)) { toast.info("Already on the list"); setDraft(""); return; }
    if ((d.extra_emails || []).length >= 10) { toast.error("Up to 10 extra recipients"); return; }
    save({ extra_emails: [...(d.extra_emails || []), e] });
    setDraft("");
  };
  const removeEmail = (e) => save({ extra_emails: (d.extra_emails || []).filter((x) => x !== e) });
  const active = d.enabled && !d.opt_out;
  const isOwner = user?.role === "owner";
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="digest-section">
      <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Mail className="h-4 w-4 text-emerald-600" /> Weekly price digest</h2>
      <p className="text-sm text-slate-500 mb-3">{d.schedule}: the biggest price rises and drops on last week's invoices, plus a few market notes — emailed so you don't need to open the app.{d.last_run ? ` Last sent ${new Date(d.last_run.finished_at).toLocaleDateString("en-GB")} (${d.last_run.emails_sent} email${d.last_run.emails_sent === 1 ? "" : "s"}).` : ""}</p>
      <div className="space-y-2 text-sm">
        {isOwner && (
          <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 cursor-pointer">
            <span>Send the digest for this organisation</span>
            <input type="checkbox" checked={d.enabled} onChange={(e) => save({ enabled: e.target.checked })} className="h-4 w-4 accent-emerald-600" data-testid="digest-org-toggle" />
          </label>
        )}
        <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 cursor-pointer">
          <span>Email me ({user?.email})</span>
          <input type="checkbox" checked={!d.opt_out} disabled={!d.enabled} onChange={(e) => save({ opt_out: !e.target.checked })} className="h-4 w-4 accent-emerald-600" data-testid="digest-me-toggle" />
        </label>
        {isOwner && (
          <div className="rounded-md border border-slate-200 px-3 py-2.5" data-testid="digest-extra-emails">
            <p className="font-medium text-slate-900">Also email</p>
            <p className="text-xs text-slate-500 mb-2">External recipients (bookkeeper, head chef, group manager) — they get the full organisation digest without needing a Supplivo account. Up to 10 addresses.</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {(d.extra_emails || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 px-2.5 py-1 text-xs font-medium" data-testid={`digest-extra-email-${e}`}>
                  {e}
                  <button type="button" onClick={() => removeEmail(e)} className="text-emerald-700 hover:text-rose-600" aria-label={`Remove ${e}`} data-testid={`digest-extra-email-remove-${e}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              <form onSubmit={(ev) => { ev.preventDefault(); addEmail(); }} className="flex items-center gap-1">
                <Input type="email" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="name@example.com" className="h-8 text-xs w-56" data-testid="digest-extra-email-input" />
                <Button type="submit" size="sm" variant="outline" className="h-8 px-2" data-testid="digest-extra-email-add"><Plus className="h-3.5 w-3.5" /></Button>
              </form>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 pt-1">
          <Button size="sm" variant="outline" onClick={test} disabled={busy} data-testid="digest-test-button"><Send className="h-3.5 w-3.5 mr-1.5" /> Send me a test now</Button>
          <span className="text-xs text-slate-500">{active ? "Active" : "Paused"} · only sent when prices actually moved</span>
        </div>
      </div>
    </section>
  );
}
